import React, { useState } from "react";
import { linkTo } from "../utils/uiUtils";
import {
  LISTING_TYPE,
  IPropertyNFTDescriptor,
  INFTDescriptor,
} from "../platformUtils";
import useMostPopularProperties from "../hooks/useMostPopularProperties";
import useTrendingProperties from "../hooks/useTrendingProperties";
import useRecommendedProperties from "../hooks/useRecommendedProperties";

// components
import RoundedButton from "../components/RoundedButton/RoundedButton";
import Navbar from "../components/Navbar/Navbar";
import PropertySearchCompact from "../components/PropertySearchCompact/PropertySearchCompact";
import SupportedMetaverseList from "../components/SupportedMetaverseList/SupportedMetaverseList";
import SupportedMetaverseListItem from "../components/SupportedMetaverseListItem/SupportedMetaverseListItem";
import SimpleBenefitDescription from "../components/SimpleBenefitDescription/SimpleBenefitDescription";
import SimpleLinkCard from "../components/SimpleLinkCard/SingleLinkCard";
import PropertyCard from "../components/PropertyCard/PropertyCard";
import ShowcasedDetailCard from "../components/ShowcasedDetailCard/ShowcasedDetailCard";
import Footer from "../components/Footer/Footer";
import TopPropertiesCarousel from "../components/TopPropertiesCarousel/TopPropertiesCarousel";

// media
import circleLogoGreenImg from "../media/circle-logo-green.svg";
import homeHeroBannerImg from "../media/home-hero-banner.png";
import axieBlueLogoImg from "../media/world-logos/axie-blue-logo.png";
import cryptovoxelsLogoImg from "../media/world-logos/cryptovoxels-logo.png";
import decentralandHighContrastLogoImg from "../media/world-logos/decentraland-high-contrast-logo.png";
import sandboxGreyBlueLogoImg from "../media/world-logos/sandbox-grey-blue-logo.png";
import walletPurpleImg from "../media/wallet-purple.svg";
import rightArrowWhiteImg from "../media/right-arrow-white.svg";
import storefrontIconGreenImg from "../media/storefront-icon-green.svg";
import percentIconGreenGradient from "../media/percent-icon-green-gradient.svg";
import cardIconGreenGradient from "../media/card-icon-green-gradient.svg";
import axsCircleLogoBlueImg from "../media/large-stylized-crypto-icons/axs-circle-logo-blue.png";
import circleGreenGradientImg from "../media/large-stylized-crypto-icons/circle-green-gradient.svg";
import circleOutlineGreenGradientImg from "../media/large-stylized-crypto-icons/circle-outline-green-gradient.svg";
import coinbaseCircleLogoBlueImg from "../media/large-stylized-crypto-icons/coinbase-circle-logo-blue.png";
import cryptovoxelsCircleLogoBlackImg from "../media/large-stylized-crypto-icons/cryptovoxels-circle-logo-black.png";
import decentralandCircleLogoImg from "../media/large-stylized-crypto-icons/decentraland-circle-logo.png";
import manaCircleIconImg from "../media/large-stylized-crypto-icons/mana-circle-icon.svg";
import sandboxCircleLogoImg from "../media/large-stylized-crypto-icons/sandbox-circle-logo.png";
import whiteOfficeBuildingImg from "../media/placeholder-imgs/white-office-building.jpeg";
import currencyIconGreenImg from "../media/currency-icon-green.svg";
import atomIconGreenImg from "../media//atom-icon-green.svg";
import barChartIconGreenImg from "../media//bar-chart-icon-green.svg";
import calendarIconGreenImg from "../media//calendar-icon-green.svg";
import keysIconGreenImg from "../media//keys-icon-green.svg";
import homeCabooseBgImg from "../media/home-caboose-bg.svg";

function Homepage() {
  return (
    <>
      <Navbar></Navbar>
      <div className="relative w-full h-full">
        <main className="w-boxed min-w-2/3 mx-auto px-4 py-16 pb-32">
          <div className="flex mb-24">
            <div className="w-120 flex-grow pb-16">
              <h1 className="font-foslin text-8xl font-bold mb-5">
                Find Your
                <br />
                Digital Home
              </h1>
              <p className="mb-14">
                The easiest place to buy and sell digital real estate.
                View&nbsp;property and get up-to-date valuations on any
                property, from any metaverse.
              </p>
              <PropertySearchCompact></PropertySearchCompact>
            </div>
            <div className="max-w-xl ml-16">
              <img
                className="h-full object-cover rounded-lg object-center"
                src={homeHeroBannerImg}
                alt=""
              />
              <ShowcasedDetailCard 
              metaverseName="Decentraland"
              postedDate="3d ago"
              propertyName="Cozy cabin on outskirts of District X"
              price="2.58"
              views="121"
              likes="32"  
              key={"1"}
              />
            </div>
          </div>
          <div className="mb-48">
            <SupportedMetaverseList>
              <SupportedMetaverseListItem
                name="Axie Infinity"
                imgSrc={axieBlueLogoImg}
                metaverseHomepageURL="https://axieinfinity.com/"
              ></SupportedMetaverseListItem>
              <SupportedMetaverseListItem
                name="The Sandbox"
                imgSrc={sandboxGreyBlueLogoImg}
                metaverseHomepageURL="https://www.sandbox.game/en/"
              ></SupportedMetaverseListItem>
              <SupportedMetaverseListItem
                name="Cryptovoxels"
                imgSrc={cryptovoxelsLogoImg}
                metaverseHomepageURL="https://www.cryptovoxels.com/"
              ></SupportedMetaverseListItem>
              <SupportedMetaverseListItem
                name="Decentraland"
                imgSrc={decentralandHighContrastLogoImg}
                metaverseHomepageURL="https://decentraland.org/"
              ></SupportedMetaverseListItem>
            </SupportedMetaverseList>
          </div>
          <div className="mb-48 flex flex-col items-center justify-center text-center">
            <div className="flex items-center justify-center mb-4">
              <img className="" src={walletPurpleImg} alt="Wallets" />
              <span className="ml-4 font-sm font-bold uppercase tracking-widest">
                Support Wallets
              </span>
            </div>
            <h2 className="font-foslin text-7xl font-bold mb-6">
              No Wallet? No Problem.
            </h2>
            <p className="max-w-3xl px-4 text-mid-grey font-medium mb-12">
              Gone are the days when only NFT and DeFi experts could participate
              in a decentralized internet. No crypto knowledge required.
            </p>
            <RoundedButton icon={rightArrowWhiteImg}>
              Create Wallet
            </RoundedButton>
          </div>
          <div className="mb-48">
            <img className="mb-10" src={storefrontIconGreenImg} alt="" />
            <div className="flex justify-stretch items-stretch">
              <div className="w-1/2 pr-16">
                <h2 className="font-foslin text-7xl font-bold mb-5">
                  One stop shop
                </h2>
                <p className="mb-7">
                  Buy, sell, and rent property instantly from any metaverse, all
                  in one place. That means an easier browsing experience, and
                  more traffic for the properties you list. Sell quicker and
                  easier than ever.
                </p>
                <div className="flex mb-12 max-w-3xl">
                  <SimpleBenefitDescription
                    name="Acre Offer"
                    icon={percentIconGreenGradient}
                  >
                    Porta non eros. Ut finibus viverra neque, sed vestibulum
                    tellus volutpat ac cras at massa.
                  </SimpleBenefitDescription>
                  <SimpleBenefitDescription
                    name="Other"
                    icon={cardIconGreenGradient}
                  >
                    Praesent consequat erat in orci lobortis, in accumsan orci
                    vestibulum nam et dictum.
                  </SimpleBenefitDescription>
                </div>
                <RoundedButton onClick={linkTo("/explore")}>
                  Browse all properties
                </RoundedButton>
              </div>
              <div className="flex w-1/2 pl-16 items-stretch justify-stretch flex-col">
                <div className="relative h-full">
                  <div className="absolute top-0 left-0 bottom-12 aspect-square">
                    <div className="relative max-h-full max-w-full grid grid-cols-3 grid-rows-3 gap-6">
                      <div className="aspect-square">
                        <img
                          className="w-full max-w-full"
                          src={decentralandCircleLogoImg}
                          alt=""
                        />
                      </div>
                      <div className="aspect-square">
                        <img
                          className="w-full max-w-full"
                          src={circleGreenGradientImg}
                          alt=""
                        />
                      </div>
                      <div className="aspect-square">
                        <img
                          className="w-full max-w-full"
                          src={sandboxCircleLogoImg}
                          alt=""
                        />
                      </div>
                      <div className="aspect-square">
                        <img
                          className="w-full max-w-full"
                          src={coinbaseCircleLogoBlueImg}
                          alt=""
                        />
                      </div>
                      <div className="aspect-square">
                        <img
                          className="w-full max-w-full"
                          src={cryptovoxelsCircleLogoBlackImg}
                          alt=""
                        />
                      </div>
                      <div className="aspect-square">
                        <img
                          className="w-full max-w-full"
                          src={circleOutlineGreenGradientImg}
                          alt=""
                        />
                      </div>
                      <div className="aspect-square">
                        <img
                          className="w-full max-w-full"
                          src={circleGreenGradientImg}
                          alt=""
                        />
                      </div>
                      <div className="aspect-square">
                        <img
                          className="w-full max-w-full"
                          src={manaCircleIconImg}
                          alt=""
                        />
                      </div>
                      <div className="aspect-square">
                        <img
                          className="w-full max-w-full"
                          src={axsCircleLogoBlueImg}
                          alt=""
                        />
                      </div>
                      <button
                        className="absolute z-10 bg-white bottom-0 left-1/2 -translate-x-1/2 py-4 px-10 rounded-full shadow-xl hover:shadow-2xl hover:scale-105 transition-all flex justify-center items-end"
                        onClick={linkTo("/explore")}
                      >
                        <div className="text-4xl italic font-black">6+</div>
                        <div className="font-semibold ml-4 tracking-widest uppercase">
                          Metaverse
                        </div>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="mb-48">
            <div className="flex justify-between items-stretch">
              <div className="w-1/2 pr-16">
                <div className="relative h-full">
                  <div className="absolute z-0 w-full h-full overflow-hidden">
                    <img
                      className="absolute rounded-lg w-full"
                      src={whiteOfficeBuildingImg}
                      alt="Example property that has been appraised by Acre"
                    />
                  </div>
                  <div className="absolute z-10 top-0 right-0 p-5 rounded-full bg-white shadow-xl translate-x-1/2 -translate-y-1/2">
                    <img
                      className="w-12"
                      src={circleLogoGreenImg}
                      alt="This property has been appraised by Acre"
                    />
                  </div>
                </div>
              </div>
              <div className="w-1/2 pl-16">
                <img className="mb-10" src={currencyIconGreenImg} alt="" />
                <h2 className="font-foslin text-7xl font-bold mb-5">
                  Valuations
                </h2>
                <p className="mb-7">
                  Acre’s property valuation system uses both traditional real
                  estate estimating techniques and modern ML algorithms to give
                  you the best insight on what your property is actually worth,
                  before purchasing.
                </p>
                <div className="flex mb-12 max-w-3xl">
                  <SimpleBenefitDescription
                    name="Acre Offer"
                    icon={percentIconGreenGradient}
                  >
                    Porta non eros. Ut finibus viverra neque, sed vestibulum
                    tellus volutpat ac cras at massa.
                  </SimpleBenefitDescription>
                  <SimpleBenefitDescription
                    name="Other"
                    icon={cardIconGreenGradient}
                  >
                    Praesent consequat erat in orci lobortis, in accumsan orci
                    vestibulum nam et dictum.
                  </SimpleBenefitDescription>
                </div>
                <RoundedButton outline grey onClick={linkTo("/explore")}>
                  Browse all properties
                </RoundedButton>
              </div>
            </div>
          </div>
          <div className="mb-48">
            <div className="flex justify-stretch items-stretch">
              <div className="w-1/2 pr-16">
                <img className="mb-10" src={currencyIconGreenImg} alt="" />
                <h2 className="font-foslin text-7xl font-bold mb-5">Rent</h2>
                <p className="mb-7">
                  Looking for a unique company party venue? Want to make some
                  extra cash off of your LANDs? We’ve got you covered. Just set
                  a rental period and acceptable collateral and start renting!
                </p>
                <div className="flex mb-12 max-w-3xl">
                  <SimpleBenefitDescription
                    name="Acre Offer"
                    icon={percentIconGreenGradient}
                  >
                    Porta non eros. Ut finibus viverra neque, sed vestibulum
                    tellus volutpat ac cras at massa.
                  </SimpleBenefitDescription>
                  <SimpleBenefitDescription
                    name="Other"
                    icon={cardIconGreenGradient}
                  >
                    Praesent consequat erat in orci lobortis, in accumsan orci
                    vestibulum nam et dictum.
                  </SimpleBenefitDescription>
                </div>
                <RoundedButton outline grey onClick={linkTo("/explore")}>
                  List a rental
                </RoundedButton>
              </div>
              <div className="w-1/2 pl-16">
                <h3 className="text-3xl font-extrabold">Browse Rentals</h3>
                <div className="relative grid grid-cols-2 gap-6">
                  <SimpleLinkCard
                    name="High Traffic"
                    icon={barChartIconGreenImg}
                    link="/explore?type=rental&tag=high-traffic"
                  ></SimpleLinkCard>
                  <SimpleLinkCard
                    name="Day Rental"
                    icon={keysIconGreenImg}
                    link="/explore?type=rental&min-duration=1"
                  ></SimpleLinkCard>
                  <SimpleLinkCard
                    name="Large Event"
                    icon={calendarIconGreenImg}
                    link="/explore?type=rental&tag=large-property"
                  ></SimpleLinkCard>
                  <SimpleLinkCard
                    name="Sci-fi"
                    icon={atomIconGreenImg}
                    link="/explore?type=rental&tag=sci-fi"
                  ></SimpleLinkCard>
                </div>
              </div>
            </div>
          </div>
          <TopPropertiesCarousel />
        </main>
        <img
          className="absolute -z-10 bottom-0 left-0 w-full"
          src={homeCabooseBgImg}
          alt=""
        />
      </div>
      <Footer></Footer>
    </>
  );
}

export default Homepage;
